/**
 * Helper functions for handling reconciliation data with the ReconciliationData JSONB structure
 */

/**
 * Check if a record is reconciled based on ReconciliationData
 * Works for both journal entries and bank transactions since they use the same column
 * @param {Object} record - Journal entry or transaction object
 * @returns {boolean} - True if the record is reconciled
 */
export const isReconciled = (record) => {
  if (!record) return false;

  // Check reconciliation format
  if (record.reconciliationData && record.reconciliationData.reconciliationId) {
    return true;
  }

  return false;
};

/**
 * Alias for backward compatibility
 */
export const isEntryReconciled = isReconciled;
export const isTransactionReconciled = isReconciled;

/**
 * Check if a transfer entry (type 5) is reconciled
 * For transfers, each half (entrada/saída) is reconciled independently since they belong to different banks
 * @param {Object} entry - Journal entry object
 * @returns {boolean} - True if this specific transfer entry is reconciled
 */
export const isTransferReconciled = (entry) => {
  if (!entry || entry.journalEntryType !== 5) {
    return isEntryReconciled(entry);
  }

  // For transfers, each half is independent - only check if this specific entry is reconciled
  // The related entries (entrada/saída) belong to different banks and are reconciled separately
  return isEntryReconciled(entry);
};

/**
 * Check if a transfer entry or its related transfer entry is reconciled
 * For transfers, we need to check both entries since deleting one affects both
 * @param {Object} entry - Journal entry object
 * @param {Array} allEntries - Array of all journal entries to find related transfer entry
 * @returns {boolean} - True if this entry or its related transfer entry is reconciled
 */
export const isTransferPairReconciled = (entry, allEntries) => {
  if (!entry || entry.journalEntryType !== 5) {
    return isEntryReconciled(entry);
  }

  // Check if current entry is reconciled
  if (isEntryReconciled(entry)) {
    return true;
  }

  // Find the related transfer entry
  let relatedEntry = null;
  if (entry.journalEntryRefId) {
    // This entry references another entry (destination entry)
    relatedEntry = allEntries.find((e) => e.id === entry.journalEntryRefId);
  } else {
    // This entry is referenced by another entry (source entry)
    relatedEntry = allEntries.find((e) => e.journalEntryRefId === entry.id);
  }

  // Check if related entry is reconciled
  if (relatedEntry && isEntryReconciled(relatedEntry)) {
    return true;
  }

  return false;
};

/**
 * Check if any entry in a list is reconciled
 * @param {Array} entryList - Array of journal entry objects
 * @param {Array} allEntries - Array of all journal entries (optional, for transfer pair checking)
 * @returns {boolean} - True if any entry is reconciled
 */
export const hasReconciledEntries = (entryList, allEntries = null) => {
  if (!Array.isArray(entryList) || entryList.length === 0) {
    return false;
  }

  return entryList.some((entry) => {
    if (entry.journalEntryType === 5) {
      // For transfers, check both entries in the pair if allEntries is provided
      if (allEntries) {
        return isTransferPairReconciled(entry, allEntries);
      }
      return isTransferReconciled(entry);
    }
    return isEntryReconciled(entry);
  });
};

/**
 * Get reconciliation ID from entry data
 * @param {Object} entry - Journal entry object
 * @returns {string|null} - Reconciliation ID or null
 */
export const getReconciliationId = (entry) => {
  if (!entry) return null;

  if (entry.reconciliationData && entry.reconciliationData.reconciliationId) {
    return entry.reconciliationData.reconciliationId;
  }

  return null;
};

/**
 * Get reconciliation status from entry data
 * @param {Object} entry - Journal entry object
 * @returns {string} - Reconciliation status
 */
export const getReconciliationStatus = (entry) => {
  if (!entry) return 'not_reconciled';

  if (entry.reconciliationData) {
    return entry.reconciliationData.status || 'not_reconciled';
  }

  return 'not_reconciled';
};

/**
 * Get reconciliation details for display
 * @param {Object} entry - Journal entry object
 * @returns {Object} - Reconciliation details
 */
export const getReconciliationDetails = (entry) => {
  if (!entry) {
    return {
      isReconciled: false,
      reconciliationId: null,
      status: 'not_reconciled',
      reconciledAt: null,
      reconciledBy: null,
    };
  }

  const reconciliationData = entry.reconciliationData;

  if (reconciliationData) {
    return {
      isReconciled: reconciliationData.status === 'reconciled',
      reconciliationId: reconciliationData.reconciliationId,
      status: reconciliationData.status || 'not_reconciled',
      reconciledAt: reconciliationData.reconciledAt || null,
      reconciledBy: reconciliationData.reconciledBy || null,
      bankTransactions: reconciliationData.bankTransactions || [],
      journalEntries: reconciliationData.journalEntries || [],
    };
  }

  return {
    isReconciled: false,
    reconciliationId: null,
    status: 'not_reconciled',
    reconciledAt: null,
    reconciledBy: null,
    bankTransactions: [],
    journalEntries: [],
  };
};

/**
 * Check if entry date is before or on the bank's reconciliation date
 * @param {Object} entry - Journal entry object with dateVencimento
 * @param {Object} bank - Bank account object with reconciliationDate
 * @returns {boolean} - True if entry is in reconciled period
 */
export const isInReconciledPeriod = (entry, bank) => {
  if (!entry || !bank || !bank.reconciliationDate || !entry.dateVencimento) {
    return false;
  }

  const { compareAsc } = require('date-fns');
  const compareResult = compareAsc(
    new Date(entry.dateVencimento),
    new Date(bank.reconciliationDate)
  );

  // compareResult <= 0 means entry date is before or equal to reconciliation date
  return compareResult <= 0;
};

/**
 * Check if any entry in a list is in reconciled period
 * @param {Array} entryList - Array of journal entry objects
 * @param {Array} banks - Array of bank account objects
 * @returns {boolean} - True if any entry is in reconciled period
 */
export const hasEntriesInReconciledPeriod = (entryList, banks) => {
  if (!Array.isArray(entryList) || entryList.length === 0 || !Array.isArray(banks)) {
    return false;
  }

  return entryList.some((entry) => {
    const bank = banks.find((b) => b.id === entry.bankAccountId);
    return bank && isInReconciledPeriod(entry, bank);
  });
};

/**
 * Check if entry can be modified (not reconciled and not in reconciled period)
 * @param {Object} entry - Journal entry object
 * @param {Object} bank - Bank account object
 * @returns {Object} - { canModify: boolean, reason: string }
 */
export const canModifyEntry = (entry, bank) => {
  // Check if entry itself is reconciled
  const entryIsReconciled =
    entry.journalEntryType === 5 ? isTransferReconciled(entry) : isEntryReconciled(entry);

  if (entryIsReconciled) {
    return {
      canModify: false,
      reason: 'Não é possível modificar lançamentos que já foram conciliados.',
    };
  }

  // Check if entry is in reconciled period
  if (isInReconciledPeriod(entry, bank)) {
    return {
      canModify: false,
      reason: `Não é possível modificar lançamentos com data até ${new Date(
        bank.reconciliationDate
      ).toLocaleDateString('pt-BR')}. Esta conta está conciliada até esta data.`,
    };
  }

  return {
    canModify: true,
    reason: null,
  };
};
